// src/pages/AdminDashboard.jsx
//
// Admin-only view of session telemetry. Pulls from:
//   GET /api/admin/overview       → headline numbers
//   GET /api/admin/users          → per-user aggregate stats
//   GET /api/admin/sessions       → recent sessions (optionally filtered by user)
//   GET /api/admin/events         → events for a specific session (drill-down)
//
// Layout:
//   - Disclaimer banner (same gold strip as the user dashboard)
//   - Header with a "Switch to user view" button → /dashboard
//   - Overview cards (4 metrics)
//   - Users table — click a row to filter sessions below
//   - Sessions table — click "events" on a row to inline-expand its events

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, ApiError } from '../api';
import './AdminDashboard.css';

export default function AdminDashboard({ theme, toggleTheme }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedUserUid, setSelectedUserUid] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [eventsBySession, setEventsBySession] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAuthError = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  // ---------------- initial + refresh load ----------------
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ov, us] = await Promise.all([
          apiGet('/api/admin/overview', user),
          apiGet('/api/admin/users', user),
        ]);
        if (cancelled) return;
        setOverview(ov || null);
        setUsers((us && us.users) || []);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          handleAuthError();
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          // Not actually an admin — bounce to user dashboard.
          navigate('/dashboard', { replace: true });
          return;
        }
        setError(
          err instanceof ApiError
            ? err.detail || err.message
            : 'Could not load admin data.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, handleAuthError, navigate, refreshKey]);

  // ---------------- sessions reload on filter change ----------------
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const qs = selectedUserUid
          ? `?user_uid=${encodeURIComponent(selectedUserUid)}&limit=200`
          : '?limit=200';
        const data = await apiGet(`/api/admin/sessions${qs}`, user);
        if (cancelled) return;
        setSessions((data && data.sessions) || []);
        setExpandedSessionId(null);
        setEventsBySession({});
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          handleAuthError();
          return;
        }
        // Sessions panel failure shouldn't blank the whole page.
        console.warn('Failed to load sessions:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, selectedUserUid, refreshKey, handleAuthError]);

  // ---------------- events drill-down ----------------
  const toggleEvents = useCallback(
    async (sessionId) => {
      if (expandedSessionId === sessionId) {
        setExpandedSessionId(null);
        return;
      }
      setExpandedSessionId(sessionId);
      if (eventsBySession[sessionId]) return; // already loaded

      try {
        const data = await apiGet(
          `/api/admin/events?session_id=${encodeURIComponent(sessionId)}&limit=500`,
          user
        );
        setEventsBySession((prev) => ({
          ...prev,
          [sessionId]: (data && data.events) || [],
        }));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          handleAuthError();
          return;
        }
        setEventsBySession((prev) => ({ ...prev, [sessionId]: [] }));
      }
    },
    [expandedSessionId, eventsBySession, user, handleAuthError]
  );

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />

      <div className="dashboard-disclaimer" role="note">
        <span className="dashboard-disclaimer__icon" aria-hidden>⚠</span>
        <span className="dashboard-disclaimer__text">
          Admin view · Session telemetry is research/product analytics only.
        </span>
      </div>

      <main className="admin">
        <div className="container">
          <div className="admin__head">
            <div>
              <span className="eyebrow">Admin · Session telemetry</span>
              <h1 className="admin__title">Activity</h1>
              <p className="admin__lead">
                Logins, time spent, and per-case interactions across all users.
              </p>
            </div>
            <div className="admin__head-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRefreshKey((k) => k + 1)}
              >
                ↻ Refresh
              </button>
              <Link to="/dashboard" className="btn btn-primary btn-sm">
                Switch to user view
              </Link>
            </div>
          </div>

          {error && <div className="admin__error">{error}</div>}

          {/* ------------- Overview cards ------------- */}
          <section className="admin__section">
            <div className="admin-cards">
              <MetricCard
                label="Users"
                value={loading ? '…' : overview?.total_users ?? 0}
              />
              <MetricCard
                label="Sessions"
                value={loading ? '…' : overview?.total_sessions ?? 0}
              />
              <MetricCard
                label="Total minutes"
                value={loading ? '…' : (overview?.total_minutes ?? 0).toFixed(1)}
              />
              <MetricCard
                label="Active now"
                value={loading ? '…' : overview?.active_now ?? 0}
                highlight={overview?.active_now > 0}
              />
            </div>
          </section>

          {/* ------------- Users table ------------- */}
          <section className="admin__section">
            <div className="admin__section-head">
              <h2 className="admin__section-title">Users</h2>
              {selectedUserUid && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSelectedUserUid(null)}
                >
                  Clear filter
                </button>
              )}
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th className="num">Logins</th>
                    <th className="num">Total time</th>
                    <th>First seen</th>
                    <th>Last seen</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && users.length === 0 ? (
                    <tr><td colSpan="6" className="admin-table__empty">Loading…</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan="6" className="admin-table__empty">No users yet.</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr
                        key={u.user_uid}
                        className={`admin-row ${selectedUserUid === u.user_uid ? 'admin-row--selected' : ''}`}
                        onClick={() => setSelectedUserUid(
                          selectedUserUid === u.user_uid ? null : u.user_uid
                        )}
                      >
                        <td><code>{u.user_email}</code></td>
                        <td className="num">{u.session_count}</td>
                        <td className="num">{formatMinutes(u.total_minutes)}</td>
                        <td>{formatDate(u.first_seen)}</td>
                        <td>{formatDate(u.last_seen)}</td>
                        <td>
                          {u.active_now ? (
                            <span className="status-pill status-pill--active">● active</span>
                          ) : (
                            <span className="status-pill">offline</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ------------- Sessions table ------------- */}
          <section className="admin__section">
            <div className="admin__section-head">
              <h2 className="admin__section-title">
                Sessions
                {selectedUserUid && (
                  <span className="admin__filter-tag">
                    filtered: {users.find((u) => u.user_uid === selectedUserUid)?.user_email || selectedUserUid}
                  </span>
                )}
              </h2>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Started</th>
                    <th className="num">Duration</th>
                    <th>Status</th>
                    <th>Ended</th>
                    <th>Events</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr><td colSpan="6" className="admin-table__empty">
                      {loading ? 'Loading…' : 'No sessions for this filter.'}
                    </td></tr>
                  ) : (
                    sessions.map((s) => (
                      <React.Fragment key={s.session_id}>
                        <tr>
                          <td><code>{s.user_email}</code></td>
                          <td>{formatDate(s.started_at)}</td>
                          <td className="num">{formatMinutes(s.duration_minutes)}</td>
                          <td>
                            <span className={`status-pill status-pill--${s.status}`}>
                              {s.status === 'active' ? '● ' : ''}{s.status}
                            </span>
                          </td>
                          <td>{s.ended_at ? formatDate(s.ended_at) : (s.end_reason || '—')}</td>
                          <td>
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => toggleEvents(s.session_id)}
                            >
                              {expandedSessionId === s.session_id ? 'hide' : 'view'}
                            </button>
                          </td>
                        </tr>
                        {expandedSessionId === s.session_id && (
                          <tr className="admin-row--events">
                            <td colSpan="6">
                              <EventLog events={eventsBySession[s.session_id]} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function MetricCard({ label, value, highlight }) {
  return (
    <div className={`metric-card ${highlight ? 'metric-card--highlight' : ''}`}>
      <div className="metric-card__label">{label}</div>
      <div className="metric-card__value">{value}</div>
    </div>
  );
}

function EventLog({ events }) {
  if (events === undefined) {
    return <div className="event-log__empty">Loading events…</div>;
  }
  if (events.length === 0) {
    return <div className="event-log__empty">No events recorded for this session.</div>;
  }
  return (
    <ol className="event-log">
      {events.slice().reverse().map((e) => (
        <li key={e.id} className="event-log__row">
          <span className="event-log__time">{formatTime(e.occurred_at)}</span>
          <span className="event-log__type">{e.event_type}</span>
          {e.event_payload && (
            <code className="event-log__payload">
              {typeof e.event_payload === 'object'
                ? JSON.stringify(e.event_payload)
                : String(e.event_payload)}
            </code>
          )}
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

function formatMinutes(mins) {
  if (mins == null) return '—';
  if (mins < 1) return `${Math.round(mins * 60)}s`;
  if (mins < 60) return `${mins.toFixed(1)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}
